import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useAuth from '../hooks/useAuth';
import { formatQty } from '../utils/recipeIngredients';

const STORAGE_PREFIX = 'recipelab-shopping-list';
const ShoppingListContext = createContext(null);

const loadItems = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const itemKey = (item) =>
  `${String(item.name || '').trim().toLowerCase()}|${String(item.measurement || '').trim().toLowerCase()}`;

export const ShoppingListProvider = ({ children }) => {
  const { auth } = useAuth();
  const storageKey = `${STORAGE_PREFIX}:${auth?.user || 'guest'}`;
  const [items, setItems] = useState([]);
  const skipSave = useRef(true);

  useEffect(() => {
    skipSave.current = true;
    setItems(loadItems(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (skipSave.current) {
      skipSave.current = false;
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify(items));
  }, [items, storageKey]);

  const addItems = useCallback((incoming, recipeName = '') => {
    const nextItems = Array.isArray(incoming) ? incoming : [incoming];
    setItems((prev) => {
      const next = [...prev];
      nextItems.forEach((raw) => {
        const name = String(raw.name || '').trim();
        if (!name) return;
        const quantity = Number(raw.quantity) || 0;
        const measurement = raw.measurement || '';
        const source = recipeName || raw.recipeName || '';
        const key = itemKey({ name, measurement });
        const existing = next.find((item) => itemKey(item) === key);
        if (existing) {
          existing.quantity = Number(existing.quantity || 0) + quantity;
          existing.checked = false;
          if (source && existing.recipeName && !existing.recipeName.includes(source)) {
            existing.recipeName = `${existing.recipeName}, ${source}`;
          } else if (source && !existing.recipeName) {
            existing.recipeName = source;
          }
        } else {
          next.push({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name,
            quantity,
            measurement,
            recipeName: source,
            checked: false,
          });
        }
      });
      return next;
    });
  }, []);

  const toggleItem = useCallback((id) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, checked: !item.checked } : item))
    );
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearChecked = useCallback(() => {
    setItems((prev) => prev.filter((item) => !item.checked));
  }, []);

  const clearAll = useCallback(() => setItems([]), []);

  const toText = useCallback(() => {
    if (!items.length) return 'Shopping list is empty.';
    return items
      .map((item) => {
        const mark = item.checked ? '[x]' : '[ ]';
        const qty = formatQty(item.quantity);
        const line = [qty, item.measurement, item.name].filter(Boolean).join(' ');
        const source = item.recipeName ? ` (${item.recipeName})` : '';
        return `${mark} ${line}${source}`;
      })
      .join('\n');
  }, [items]);

  const uncheckedCount = useMemo(
    () => items.filter((item) => !item.checked).length,
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      uncheckedCount,
      addItems,
      toggleItem,
      removeItem,
      clearChecked,
      clearAll,
      toText,
    }),
    [items, uncheckedCount, addItems, toggleItem, removeItem, clearChecked, clearAll, toText]
  );

  return (
    <ShoppingListContext.Provider value={value}>
      {children}
    </ShoppingListContext.Provider>
  );
};

export const useShoppingList = () => {
  const ctx = useContext(ShoppingListContext);
  if (!ctx) {
    throw new Error('useShoppingList must be used within ShoppingListProvider');
  }
  return ctx;
};

export default ShoppingListContext;
